// ContactRefField.tsx — wählt einen Gegen-Kontakt für eine Verknüpfung:
// (1) bestehenden Kontakt SUCHEN (GET /contact?search, debounced) ODER
// (2) neu EINTIPPEN (Freitext) — beides OHNE jeden POST. Der frei eingetippte
// Kontakt wird erst beim Speichern des Formulars angelegt (persistRelations.ts).
//
// Liefert/empfängt einen ContactRef über value/onChange. Kein eigener Backend-
// Schreibzugriff — das ist der bewusste Unterschied zum alten RelationContactPicker.

import * as React from "react";
import { geschlechtAusAnrede } from "../../utils/anredeGeschlecht";
import { FieldRow, LabeledInput, LabeledSelect } from "../forms/formPrimitives";
import { LegalFormSelect } from "../forms/widgets/LegalFormSelect";
import { BrancheZweiSelect } from "../forms/widgets/BrancheZweiSelect";
import { findGruppe, formatGruppeMitFreitext, useBrancheGruppen } from "../../data/brancheGruppen";
import type { BrancheGruppenWahl } from "../forms/widgets/brancheGruppenLogik";
import {
  fetchLegalFormOptions,
  rechtsformAusName,
  type LegalFormOption,
} from "../../utils/legalFormOptions";
import { useRelStyles } from "./styles";
import { REFINED } from "../risikoanalyseStep/tokens";
import FirmendatenBefuellenDialog from "../FirmendatenBefuellenDialog";
import { gemerktesLogo, logoMerken, logoVergessen } from "../../utils/logoVorschlag";
import { apiUrl } from "../../utils/api";
import { useEntprellteSuche, holeJsonListe } from "../../hooks/useEntprellteSuche";
import { SuchStatus } from "../forms/widgets/SuchStatus";
import { katalogLaden } from "../../utils/katalogCache";
import {
  KONTAKTTYP_PERSON,
  KONTAKTTYP_PERSON_LABEL,
  contactTypeLabel,
} from "../../utils/contactTypeLabel";
import type { ContactRef } from "./types";
import { refDisplayName, weitereBranchenNormalisieren } from "./types";
import { PhoneInput } from "../forms/widgets/PhoneInput";
import { KontaktSprungContext } from "../forms/KontaktSprung";
import { nameAusSuche, nameMitNachnameVorschlag } from "../../utils/nameAusSuche";
import { VollanlageWeiterKnopf } from "../forms/VollanlageWeiter";

const SearchIcon = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
    <circle cx="9" cy="9" r="6" />
    <path d="M14 14l3.5 3.5" />
  </svg>
);


function nameOf(c: any): string {
  return c?.displayName || `${c?.lastname ?? ""}${c?.firstName ? ", " + c.firstName : ""}`.replace(/^,\s*|,\s*$/g, "").trim() || "—";
}

/**
 * Branchen-Auswahl der Firmen-Schnellanlage — dieselbe Zwei-Ebenen-Auswahl wie
 * in der Vollanlage und der Akte.
 *
 * Eigene Komponente, weil `useBrancheGruppen` den Katalog lädt: So passiert das
 * nur, wenn wirklich eine Firma eingetippt wird — nicht bei jedem Kontakt-Feld
 * auf der Seite.
 *
 * Die ERSTE Auswahl ist die Hauptbranche und wandert in `branchengruppeId` +
 * `brancheFreitext` + `branche` (→ `Contact.industry`); die weiteren reicht
 * `createContactFromRef` nach dem Anlegen über `/contact/:id/branchengruppen`
 * nach.
 *
 * Exportiert, damit der Freitext-Rundlauf (Widget → `onPatch` → `value` →
 * Widget) prüfbar ist, ohne die ganze Kontaktsuche samt Backend-Aufrufen
 * hochzufahren.
 */
export const FirmaBranchenFeld: React.FC<{
  value: ContactRef;
  onPatch: (p: Partial<ContactRef>) => void;
  idBase: string;
}> = ({ value, onPatch, idBase }) => {
  const { rows } = useBrancheGruppen();

  const auswahl: BrancheGruppenWahl[] = React.useMemo(() => {
    const haupt = value.branchengruppeId
      ? [{ branchengruppeId: value.branchengruppeId, freitext: value.brancheFreitext ?? null }]
      : [];
    const weitere = weitereBranchenNormalisieren(value.weitereBranchengruppen).map((w) => ({
      branchengruppeId: w.branchengruppeId,
      freitext: w.freitext ?? null,
    }));
    return [...haupt, ...weitere];
  }, [value.branchengruppeId, value.brancheFreitext, value.weitereBranchengruppen]);

  return (
    <BrancheZweiSelect
      idBase={`${idBase}-branche`}
      // Auch im Popup sind MEHRERE Branchen möglich (PO: „man muss mehrere
      // Branchen erstellen können") — nur eben sparsam: solange es bei einem
      // Block bleibt, kommt außer „+ weitere Branche" nichts dazu.
      kompakt
      value={auswahl}
      onChange={(next) => {
        const haupt = next[0];
        const gruppe = haupt ? findGruppe(rows, haupt.branchengruppeId) : undefined;
        onPatch({
          branchengruppeId: haupt?.branchengruppeId,
          brancheFreitext: haupt?.freitext ?? "",
          branche: gruppe ? formatGruppeMitFreitext(gruppe, haupt?.freitext) : "",
          // MIT Freitext: Ohne ihn verlor das Popup die Beschreibung einer
          // Auffanggruppe, die nicht die Hauptbranche ist, bei jedem
          // Tastendruck — das Feld rendert controlled aus `value` und stand
          // nach jedem Zeichen wieder leer da.
          weitereBranchengruppen: next.slice(1).map((a) => ({
            branchengruppeId: a.branchengruppeId,
            freitext: a.freitext ?? null,
          })),
        });
      }}
    />
  );
};

export function ContactRefField({
  wantType,
  value,
  onChange,
  idBase,
  allowTypeSwitch,
  firmaExtraFields,
  personOnly,
  excludeIds,
  suchPlaceholder,
  mitBefuellen,
  showPersonAddress,
  sameHousehold,
  zentrale,
  nachnameVorschlag,
}: {
  wantType: "Person" | "Firma";
  value: ContactRef | null;
  onChange: (r: ContactRef | null) => void;
  idBase: string;
  /** erlaubt im Eintipp-Modus die Wahl Person/Firma (z. B. Gesellschafter). */
  allowTypeSwitch?: boolean;
  /** Firma-Eintippen mit den Pflichtfeldern der Firmen-Anlage (Branche, Rechtsform, Tel, E-Mail). */
  firmaExtraFields?: boolean;
  /** nur Personen-Treffer anzeigen (Firmen aus der Suche filtern). */
  personOnly?: boolean;
  /** contactIds, die aus der Suche ausgeblendet werden (bereits verknüpfte Kontakte). */
  excludeIds?: number[];
  /**
   * Abweichender Suchtext. Ein Verein ist datenmodellhaft eine Firma — wer eine
   * Vereinsmitgliedschaft erfasst, sucht aber keine „Firma".
   */
  suchPlaceholder?: string;
  /**
   * Zeigt neben der Internet-Adresse den Knopf „Daten befüllen" — dieselbe
   * Anreicherung wie in der Firmen-Anlage.
   *
   * Wer hier eine Firma anlegt, hat meistens nur den Namen und die Webseite;
   * Anschrift, Rechtsform und Rufnummern von Hand nachzutragen ist genau die
   * Arbeit, die der Dialog abnimmt. Bewusst ein Schalter und kein Standard: In
   * den Beziehungs-Listen wird oft nur ein Name festgehalten, und ein Knopf,
   * der einen Dialog öffnet, wäre dort mehr Angebot als Hilfe.
   */
  mitBefuellen?: boolean;
  /** Zeigt Anschrift-Felder für neu anzulegende Privatpersonen (z. B. außerhalb des Haushalts). */
  showPersonAddress?: boolean;
  /** Gibt an, ob die Person im selben Haushalt lebt (Anschrift wird geerbt). */
  sameHousehold?: boolean;
  /**
   * Zentralnummer(n) der Firma, an der die Person angelegt wird — für die
   * automatische Durchwahl der geschäftlichen Telefonnummer (beim Verlassen).
   */
  zentrale?: readonly (string | null | undefined)[] | string | null;
  /** D4 (Plan 2026-09-30): vorbelegter Nachname beim Neu-Eintippen einer Person. */
  nachnameVorschlag?: string;
}) {
  const s = useRelStyles();
  // Nur in der Kontaktakte gesetzt (AkteAllgemeinAnlage) — in der Neuanlage
  // würde ein Sprung die Eingaben verwerfen, daher dort kein „Akte öffnen".
  const springe = React.useContext(KontaktSprungContext);
  const [befuellenOffen, setBefuellenOffen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [sals, setSals] = React.useState<{ id: number; salutation: string }[]>([]);
  const [titles, setTitles] = React.useState<{ id: number; title: string }[]>([]);

  const manualType: "Person" | "Firma" = value?.type ?? wantType;

  // Rechtsform aus dem eingetippten Firmennamen erkennen — dieselbe Mechanik
  // wie in der Vollanlage („… GmbH" → GmbH). Nur vorbelegen, solange der
  // Anwender die Rechtsform nicht selbst gewählt hat.
  const [legalFormOptions, setLegalFormOptions] = React.useState<LegalFormOption[]>([]);
  const legalFormTouchedRef = React.useRef(false);
  React.useEffect(() => {
    if (!firmaExtraFields) return;
    let aktiv = true;
    fetchLegalFormOptions()
      .then((opts) => { if (aktiv) setLegalFormOptions(opts); })
      .catch(() => { /* ohne Optionen entfällt die Erkennung still */ });
    return () => { aktiv = false; };
  }, [firmaExtraFields]);

  // Die Suche liefert Personen UND Firmen; angezeigt wird nur, was hier gesucht
  // ist. „Firma suchen …" zeigte bis 08/2026 auch Privatkontakte an und ließ sie
  // sogar wählen — der Gegenkontakt hatte dann den falschen Typ. Ausgenommen ist
  // `allowTypeSwitch`: dort ist Person ODER Firma ausdrücklich gewollt.
  const restrictType: "Person" | "Firma" | null = personOnly
    ? "Person"
    : allowTypeSwitch
      ? null
      : wantType;

  // Entprellte Suche — Lade-Anzeige endet immer, Fehler sind sichtbar und
  // wiederholbar (useEntprellteSuche). Im Auswahl-/Eintipp-Modus nicht suchen.
  const suche = useEntprellteSuche<any>(
    q,
    !value,
    async (term, signal) => {
      // Mit `limit` und — wo nur ein Typ gesucht ist — Typ-Filter am Server:
      // Ohne Limit lieferte der Server bis zu 100 voll hydrierte Kontakte
      // (PROD 01.10.2026: „Court" ~82 s), und ein clientseitiger Typ-Filter
      // auf einer gekappten Liste verlöre die gesuchten Firmen.
      // Schlanke Form (`include=`): Die Trefferliste braucht nur Typ, Status,
      // Rechtsform, Anrede und Stammfelder — die volle Form lud Verträge und
      // Beziehungen mit und brauchte für „court“ auf PROD 80 s (01.10.2026).
      const params: Record<string, string> = { search: term, limit: "30", include: "" };
      if (restrictType) params.filters = JSON.stringify({ Typ: restrictType });
      // Die Firmensuche („Unternehmen", „Verein") sucht nur im Namen — nicht
      // in E-Mail, Telefon oder Adresse der Firma.
      // Courté-weit: keine persönlichen Kontakte anderer Nutzer, auch nicht
      // für den admin, der sie sonst sehen darf. Eigene bleiben auffindbar.
      if (restrictType === "Firma") {
        params.nurName = "1";
        params.ohneFremdePersoenliche = "1";
      }
      const list = await holeJsonListe(apiUrl("contact", params), signal);
      const filtered = restrictType
        ? list.filter((c: any) => c?.type?.type === restrictType)
        : list;
      return filtered.slice(0, 12);
    },
    { schluessel: restrictType ?? "" },
  );
  const results = suche.treffer;

  // Anreden + Titel für den Eintipp-Modus (Person) lazy laden.
  React.useEffect(() => {
    if (!(value?.manual && manualType === "Person")) return;
    if (sals.length === 0) {
      katalogLaden<unknown>("contactsalutation")
        .then((d) => setSals(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
    if (titles.length === 0) {
      katalogLaden<unknown>("contact-title")
        .then((d) => setTitles(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
  }, [value?.manual, manualType, sals.length, titles.length]);

  // (A) Bestehender Kontakt gewählt → Chip.
  if (value?.contactId) {
    return (
      <div className={s.chip}>
        <div className={s.chipMain}>
          <span className={s.chipName}>{refDisplayName(value)}</span>
          <span className={s.chipType}>{contactTypeLabel(value.type)}{value.salutationLabel ? ` · ${value.salutationLabel}` : ""}</span>
        </div>
        <div className={s.chipActions}>
          {/* T-20: Die Daten der Person (Telefon, E-Mail …) pflegt man in ihrer
              Akte. Kein <button>: muss auch in der gesperrten Akte
              (<fieldset disabled>) klickbar bleiben — wie KontaktName. */}
          {springe && (
            <span
              role="link"
              tabIndex={0}
              aria-label={`Akte von ${refDisplayName(value)} öffnen`}
              title="Akte des Kontakts öffnen — dort ändern Sie z. B. Telefon oder E-Mail"
              className={s.chipChange}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                springe(value.contactId as number);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  springe(value.contactId as number);
                }
              }}
            >
              Akte öffnen
            </span>
          )}
          {/* Früher „ändern" — wurde als „Daten der Person ändern" verstanden,
              tauscht aber die verknüpfte Person aus (T-20). */}
          <button
            type="button"
            className={s.chipChange}
            title="Verknüpft einen anderen Kontakt — die Daten der Person ändern Sie in ihrer Akte"
            onClick={() => { setQ(""); onChange(null); }}
          >
            anderen Kontakt wählen
          </button>
        </div>
      </div>
    );
  }

  // (B) Eintipp-Modus (manuell) → Freitext-Felder, KEIN POST.
  if (value?.manual) {
    const patch = (p: Partial<ContactRef>) => onChange({ ...value, ...p });
    return (
      <div className={s.refField}>
        {allowTypeSwitch && (
          <LabeledSelect
            label="Typ"
            name={`${idBase}-type`}
            value={manualType}
            onChange={(v) => onChange({ ...value, type: v as "Person" | "Firma" })}
            options={[
              { value: KONTAKTTYP_PERSON, label: KONTAKTTYP_PERSON_LABEL },
              { value: "Firma", label: "Firma" },
            ]}
            placeholder="Typ"
          />
        )}
        {manualType === "Firma" ? (
          <>
            <LabeledInput
              label="Firmenname"
              name={`${idBase}-firma`}
              required
              value={value.lastName ?? ""}
              onChange={(v) => {
                // Nur der eindeutige Suffix-Treffer wird gesetzt. Bei einem
                // abgeschnittenen 30-Zeichen-Namen und bei mehrdeutigen Formen
                // (SE, a.G.) bleibt das Feld leer — hier ist kein Platz für
                // einen Hinweis, und eine falsche Rechtsform ist teurer als gar
                // keine.
                const erkannt =
                  firmaExtraFields && !legalFormTouchedRef.current && legalFormOptions.length
                    ? rechtsformAusName(v, legalFormOptions)
                    : null;
                const hit =
                  erkannt && erkannt.befund.konfidenz === "SICHER" ? erkannt.option : null;
                patch({
                  lastName: v,
                  ...(hit ? { legalForm: hit.legalForm, legalFormId: hit.id } : {}),
                });
              }}
              placeholder="Muster GmbH"
            />
            {firmaExtraFields && (
              <>
                <FieldRow columns={2}>
                  <LegalFormSelect
                    label="Rechtsform"
                    name={`${idBase}-legalform`}
                    required
                    value={value.legalForm ?? ""}
                    onChange={(v, id) => {
                      legalFormTouchedRef.current = true;
                      patch({ legalForm: v, legalFormId: id ? Number(id) : undefined });
                    }}
                    freitext={value.rechtsform ?? ""}
                    onFreitextChange={(v) => patch({ rechtsform: v })}
                  />
                </FieldRow>
                {/* Dieselbe Mehrfachauswahl wie in der Vollanlage: Branche und
                    Spezialisierungen in EINER Zeile (PO 17.08.2026). Vorher
                    standen hier drei kaskadierende Dropdowns untereinander —
                    im schmalen Popup drei volle Zeilen für eine Angabe. */}
                <FirmaBranchenFeld value={value} onPatch={patch} idBase={idBase} />
              </>
            )}
            {/* Pflicht sind nur Firmenname und Rechtsform (PO 17.08.2026) —
                dieselbe Regel wie in der Vollanlage (newCompanyRequired.ts).
                Telefon und E-Mail trugen hier noch einen Stern, den es dort seit
                der PO-Notiz vom 02.08. nicht mehr gibt: Wer eine Firma aus einer
                Empfehlung oder einem Registerauszug anlegt, hat oft weder das
                eine noch das andere. */}
            <FieldRow columns={2}>
              <PhoneInput actionButton={false} kind="landline" geschaeftlich label="Telefon" name={`${idBase}-tel`} value={value.phone ?? ""} onChange={(v) => patch({ phone: v })} />
              <PhoneInput actionButton={false} kind="mobile" geschaeftlich label="Mobil" name={`${idBase}-mobile`} value={value.mobile ?? ""} onChange={(v) => patch({ mobile: v })} />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="E-Mail" name={`${idBase}-mail`} value={value.email ?? ""} onChange={(v) => patch({ email: v })} />
              <LabeledInput label="Website" name={`${idBase}-web`} value={value.web ?? ""} onChange={(v) => patch({ web: v })} placeholder="https://…" />
            </FieldRow>
            {mitBefuellen && (
              <FieldRow columns={2}>
                <div />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className={s.ghostBtn}
                    style={{ whiteSpace: "nowrap" }}
                    onClick={() => setBefuellenOffen(true)}
                    disabled={!String(value.lastName ?? "").trim() && !String(value.web ?? "").trim()}
                    title="Anschrift, Rechtsform und Rufnummern aus dem Impressum übernehmen"
                  >
                    Daten befüllen
                  </button>
                </div>
              </FieldRow>
            )}
          </>
        ) : (
          <>
            <FieldRow columns={2}>
              <LabeledSelect
                label="Anrede"
                name={`${idBase}-sal`}
                value={value.salutationId != null ? String(value.salutationId) : ""}
                onChange={(v) => {
                  const id = v ? Number(v) : undefined;
                  const lbl = sals.find((x) => x.id === id)?.salutation;
                  patch({
                    salutationId: id,
                    salutationLabel: lbl,
                    gender: geschlechtAusAnrede(lbl) ?? value.gender,
                  });
                }}
                options={sals.map((x) => ({ value: String(x.id), label: x.salutation }))}
                placeholder="—"
              />
              <LabeledSelect
                label="Titel"
                name={`${idBase}-title`}
                value={value.titleId != null ? String(value.titleId) : ""}
                onChange={(v) => {
                  const id = v ? Number(v) : undefined;
                  const lbl = titles.find((x) => x.id === id)?.title;
                  patch({ titleId: id, titleLabel: lbl });
                }}
                options={titles.map((x) => ({ value: String(x.id), label: x.title }))}
                placeholder="— Kein Titel —"
              />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="Vorname" name={`${idBase}-vn`} value={value.firstName ?? ""} onChange={(v) => patch({ firstName: v })} />
              <LabeledInput label="Nachname" name={`${idBase}-nn`} required value={value.lastName ?? ""} onChange={(v) => patch({ lastName: v })} />
            </FieldRow>
            {showPersonAddress && (
              <>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Straße und Hausnummer"
                    name={`${idBase}-street`}
                    value={value.street ?? ""}
                    onChange={(v) => patch({ street: v })}
                    placeholder="Musterstraße 1"
                  />
                  <LabeledInput
                    label="Postleitzahl"
                    name={`${idBase}-plz`}
                    value={value.postalCode ?? ""}
                    onChange={(v) => patch({ postalCode: v })}
                    placeholder="12345"
                  />
                </FieldRow>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Ort"
                    name={`${idBase}-city`}
                    value={value.city ?? ""}
                    onChange={(v) => patch({ city: v })}
                    placeholder="Aachen"
                  />
                  <div />
                </FieldRow>
              </>
            )}
            {sameHousehold && (
              <div style={{ fontSize: "0.82rem", color: REFINED.ink2, fontStyle: "italic", marginTop: "-4px" }}>
                Anschrift wird aus dem gemeinsamen Haushalt übernommen.
              </div>
            )}
            <FieldRow columns={2}>
              <PhoneInput actionButton={false} kind="landline" label="Telefon (privat)" name={`${idBase}-tel-p`} value={value.phonePrivate ?? ""} onChange={(v) => patch({ phonePrivate: v })} />
              <PhoneInput actionButton={false} kind="landline" geschaeftlich zentrale={zentrale} label="Telefon (geschäftlich)" name={`${idBase}-tel-g`} value={value.phoneWork ?? ""} onChange={(v) => patch({ phoneWork: v })} />
            </FieldRow>
            <FieldRow columns={2}>
              <PhoneInput actionButton={false} kind="mobile" label="Mobil (privat)" name={`${idBase}-mob-p`} value={value.mobilePrivate ?? ""} onChange={(v) => patch({ mobilePrivate: v })} />
              <PhoneInput actionButton={false} kind="mobile" geschaeftlich label="Mobil (geschäftlich)" name={`${idBase}-mob-g`} value={value.mobileWork ?? ""} onChange={(v) => patch({ mobileWork: v })} />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="E-Mail (privat)" name={`${idBase}-mail-p`} value={value.emailPrivate ?? ""} onChange={(v) => patch({ emailPrivate: v })} />
              <LabeledInput label="E-Mail (geschäftlich)" name={`${idBase}-mail-g`} value={value.emailWork ?? ""} onChange={(v) => patch({ emailWork: v })} />
            </FieldRow>
          </>
        )}
        {/* „Daten befüllen" füllt genau die Felder, die der Ref kennt:
            Firmenname, Rechtsform, Telefon, E-Mail, Website, Anschrift. */}
        {mitBefuellen && (
          <FirmendatenBefuellenDialog
            open={befuellenOffen}
            eingabe={{
              name: String(value.lastName ?? "").trim(),
              ort: null,
              plz: null,
              strasse: null,
              website: value.web || null,
              email: value.email || null,
            }}
            vorhanden={{
              name: String(value.lastName ?? "").trim() || undefined,
              website: value.web || undefined,
              email: value.email || undefined,
              phone: value.phone || undefined,
              rechtsform: value.legalForm || undefined,
            }}
            onClose={() => setBefuellenOffen(false)}
            // Logo-Vorschlag: gemerkt, übernommen wird es nach dem Anlegen der
            // Firma (persistRelations → logoNachreichen).
            logo={{
              vorhanden: !!gemerktesLogo(value.logoVorschlag),
              onJa: (bild) => {
                logoVergessen(value.logoVorschlag);
                patch({ logoVorschlag: logoMerken(bild) });
              },
            }}
            onUebernehmen={(felder) => {
              patch({
                ...(felder.name ? { lastName: felder.name } : {}),
                ...(felder.phone ? { phone: felder.phone } : {}),
                ...(felder.email ? { email: felder.email } : {}),
                ...(felder.website ? { web: felder.website } : {}),
                ...(felder.rechtsform ? { legalForm: felder.rechtsform } : {}),
                ...(felder.street ? { street: felder.street } : {}),
                ...(felder.postalcode ? { postalCode: felder.postalcode } : {}),
                ...(felder.city ? { city: felder.city } : {}),
              });
              setBefuellenOffen(false);
            }}
          />
        )}
        {/* Vollanlage mit allem, was hier steht (User 02.10.2026). Danach ist
            der Kontakt angelegt und steht hier als bestehender — beim Speichern
            entsteht nur noch die Beziehung. */}
        <VollanlageWeiterKnopf
          kontakt={{ ...value, type: manualType }}
          imHaushalt={sameHousehold}
          onCreated={({ ref }) => onChange(ref)}
        />
        <button type="button" className={s.switchLink} onClick={() => { setQ(""); onChange(null); }}>← stattdessen bestehenden Kontakt suchen</button>
      </div>
    );
  }

  // (C) Suche (Default). Bereits verknüpfte Kontakte (excludeIds) ausblenden.
  const visibleResults =
    excludeIds && excludeIds.length ? results.filter((c) => !excludeIds.includes(c.id)) : results;
  return (
    <div className={s.refField}>
      <div className={s.searchWrap}>
        <span className={s.searchIcon}>{SearchIcon}</span>
        <input
          className={s.searchInput}
          // Gesucht wird nach Kontakttyp — und der heißt in der Oberfläche
          // „Privat(kontakt)", nicht „Person" (siehe utils/contactTypeLabel.ts).
          placeholder={
            suchPlaceholder ??
            (restrictType === KONTAKTTYP_PERSON
              ? "Privatkontakt suchen …"
              : restrictType === "Firma"
                ? "Firma suchen …"
                : "Privatkontakt oder Firma suchen …")
          }
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <SuchStatus suche={suche} term={q} className={s.msg} anzahl={visibleResults.length} />
      {visibleResults.length > 0 && (
        <div className={s.results}>
          {visibleResults.map((c) => (
            <button
              type="button"
              key={c.id}
              className={s.resultRow}
              onClick={() => {
                setQ(""); // leert auch die Trefferliste (useEntprellteSuche)
                onChange({
                  contactId: c.id,
                  type: (c.type?.type as "Person" | "Firma") || wantType,
                  displayName: nameOf(c),
                  // Die Rechtsform lag im Suchergebnis schon bereit (Contact.legalForm
                  // wird eager geladen), wurde hier aber verworfen. Ohne sie kann
                  // keine der aufrufenden Komponenten prüfen, welche Rollen an dieser
                  // Firma überhaupt zulässig sind — und nicht erkennen, dass die
                  // Rechtsform fehlt.
                  legalForm: c.legalForm?.legalForm ?? undefined,
                  legalFormId: c.legalForm?.id ?? undefined,
                  gender: c.gender,
                  salutationLabel: c.salutation?.salutation,
                  // Für Alter im Beziehungsdiagramm der Anlage.
                  birthDate: c.birthDate ? String(c.birthDate).slice(0, 10) : undefined,
                  // Kreuz im Organigramm der Anlage (User 28.09.2026).
                  ...(c.deceased === true || c.deceasedAt ? { deceased: true } : {}),
                  // Spalte „Kontaktstatus" der Beziehungslisten (User 28.09.2026).
                  ...(c.status?.status || typeof c.status === "string"
                    ? { status: String(c.status?.status ?? c.status) }
                    : {}),
                });
              }}
            >
              <span className={s.resultName}>{nameOf(c)}</span>
              <span className={s.resultType}>{contactTypeLabel(c.type?.type) || ""}</span>
            </button>
          ))}
        </div>
      )}
      {/* Das „neu eintippen" stand bis 09/2026 unter dem Vorbehalt
          `ohneNeuEintippen`; die einzige Stelle, die ihn setzte — der
          Arbeitgeber in der Kontakt-Anlage — legt jetzt selbst an. Ein
          Wahlschalter ohne Nutzer ist eine Falle für den Nächsten. */}
      <button
        type="button"
        className={s.newLink}
        onClick={() => {
          const raw = q.trim();
          if (manualType === "Firma" || wantType === "Firma") {
            onChange({
              type: "Firma",
              manual: true,
              lastName: raw,
            });
          } else {
            const { vorname: firstName, nachname: lastName } = nachnameVorschlag
              ? nameMitNachnameVorschlag(raw, nachnameVorschlag)
              : nameAusSuche(raw);
            onChange({
              type: "Person",
              manual: true,
              firstName,
              lastName,
            });
          }
        }}
      >
        + Kontakt ist noch nicht im System — neu eintippen
      </button>
    </div>
  );
}
